
import { Component, Vue, Watch } from "vue-property-decorator";
import { MealCountDtoViewModel } from "@/view-models";
import { eventBus, mcEvents, mcDate, mcLogger } from "@/utils";
import { types } from "@/store";
import { routePaths } from "@/router";
import {
  MergeLines,
  MergeServingLineRequest,
  MealCountDto,
  FeatureFlagInfo,
  ServingLine
} from "@/models";
import { ValidationErrors } from "fluentvalidation-ts/dist/ValidationErrors";
import moment from "moment";

@Component
export default class McMslPrompt extends Vue {
  private console: Console = mcLogger.getConsole(
    this.constructor.name,
    mcLogger.colors.McMslPrompt
  );
  counts = new MealCountDtoViewModel(new MealCountDto());
  mealTypeId: string = "";
  fluent: ValidationErrors<MealCountDto> = {};
  mslTitle: string = "";
  mslSiteInfo = new FeatureFlagInfo();

  /** Other user Serving Lines selected to be merged into the current users Serving Line or discard */
  selected: ServingLine[] = [];
  message: string = "";
  discard: boolean = false;
  query = {};

  mounted() {
    eventBus.$on(mcEvents.sendMealCounts, this.getMealCounts);
    eventBus.$on(mcEvents.serveClicked, this.checkForPrompt);

    this.$root.$on("bv::modal::hide", (bvEvent, modalId) => {
      this.selected.length = 0;
      this.update();
    });
  }

  beforeDestroy() {
    eventBus.$off(mcEvents.sendMealCounts);
    eventBus.$off(mcEvents.serveClicked);
  }

  get isOnline() {
    return this.$store.getters[types.state.isOnline];
  }

  @Watch("isOnline")
  goneOffline(value) {
    if (value === false) {
      this.selected = [];
    }
  }

  // create and save your line before you can merge other lines!
  get userHasLine() {
    const myLine = this.counts.userServingLine;
    return myLine !== undefined;
  }

  // create and save your line before you can merge other lines!
  get canMerge() {
    const myLine = this.counts.userServingLine;
    const otherLines = this.counts.nonUserServingLines;
    const result = myLine !== undefined && otherLines.length > 0;
    return result;
  }

  get currentUser() {
    return this.$store.getters[types.state.user];
  }

  getMealCounts(countVm: MealCountDtoViewModel) {
    this.counts = countVm;
  }

  createMergeServingLineRequest(discard) {
    let mergeLines: MergeLines[] = new Array();

    this.selected.forEach(servingLine => {
      /*
      * Find our current user's serving line that has not been merged.
      * We will merge the other serving line(s) into this one.
      */
      let mergeIntoLine = this.counts.mealCounts.servingLines.find(
        u => u.servedBy == this.currentUser.userId
        && u.mergedIntoTrackingId === undefined
      );

      if (servingLine !== undefined && mergeIntoLine !== undefined) {
        servingLine.mergedIntoTrackingId = mergeIntoLine.trackingId;
        servingLine.discarded = discard;

        let mergeInfo = new MergeLines(
          servingLine.trackingId,
          mergeIntoLine.trackingId,
          discard,
          moment.utc().format()
        );

        mergeLines.push(mergeInfo);
      }
    });

    let request = new MergeServingLineRequest(
      this.counts.mealCounts.trackingId,
      mergeLines
    );

    return request;
  }

  checkForPrompt(query) {
    this.query = query;
    this.mslSiteInfo = this.getMslSite(this.counts.mealCounts.siteId);
    const isMslSite = this.mslSiteInfo.msl.isActive;

    if (
      (isMslSite && this.counts.nonUserServingLines.length > 0) ||
      (!isMslSite && this.counts.mealCounts.servingLines.length > 1)
    ) {
      this.mslTitle = " Other Servers: " + this.counts.mealType;
      this.$bvModal.show("mslPrompt");
    } else {
      this.continueToServe(query);
    }
  }

  async discardCounts() {
    this.discard = true;

    let request = this.createMergeServingLineRequest(this.discard);

    await this.$store.dispatch(
      types.actions.updateCountDto,
      this.counts.mealCounts
    );
    await this.$store.dispatch(types.actions.updateMergeServingLines, request);

    eventBus.$emit(mcEvents.linesMerged);
    this.continueToServe(this.query);
  }

  async mergeCounts() {
    this.discard = false;
    let request = this.createMergeServingLineRequest(this.discard);

    await this.$store.dispatch(
      types.actions.updateCountDto,
      this.counts.mealCounts
    );
    await this.$store.dispatch(types.actions.updateMergeServingLines, request);

    eventBus.$emit(mcEvents.linesMerged);
    this.continueToServe(this.query);
  }

  countFormat(servingLine) {
    let fullName =
      servingLine.servedByFirstName + " " + servingLine.servedByLastName;
    let totalCounts =
      servingLine.mealCountServedFirstMeal +
      servingLine.mealCountServedSecondMeal +
      servingLine.mealCountServedAdultProgram +
      servingLine.mealCountServedAdultNonProgram +
      servingLine.mealCountIncomplete +
      servingLine.mealCountWaste +
      servingLine.mealCountLeftover +
      servingLine.mealCountAdditionalRequested;
    return `${fullName} (${totalCounts}) `;
  }

  userDateTime(servingLine) {
    const local = mcDate.utcToLocal(servingLine.modifiedDateTimeUtc);
    const localTime = mcDate.isToday(local)
      ? mcDate.toTime(local)
      : mcDate.toDateTime(local);

    return `${localTime}`;
  }

  continueToServe(query) {
    this.$router.push({
      name: routePaths.serveMeals.name,
      params: this.query
    });
  }

  getMslSite(siteId) {
    return this.$store.getters[types.state.featureFlags].find(
      p => p.siteId == siteId
    );
  }

  currentUserHasCounts(counts): boolean {
    let hasCounts =
      this.counts.mealCounts.servingLines.find(
        u =>
          u.servedBy == this.currentUser.userId &&
          u.mergedIntoTrackingId === undefined
      ) === undefined
        ? false
        : true;
    return hasCounts;
  }

  usersSelected(servingLine) {
    return this.selected.find(u => u.servedBy == servingLine.servedBy);
  }

  openTooltip(string) {
    this.message = string;
    this.closeTooltip();
    this.$root.$emit("bv::show::tooltip", this.message);
  }

  closeTooltip() {
    this.$root.$emit("bv::hide::tooltip");
  }

  showLines(servingLine) {
    let show =
      servingLine.servedBy !== this.currentUser.userId &&
      servingLine.isMerged == false;
    return show;
  }

  update() {
    this.$forceUpdate();
  }
}
